import { useContext } from "react";
import Card from "react-bootstrap/Card";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Dropdown from "react-bootstrap/Dropdown";
import {
  BsInfoCircleFill,
  BsSun,
  Bs0Circle,
  BsXCircle,
  BsBatteryHalf,
  BsBox,
  BsWater,
} from "react-icons/bs";
import { Alert, LoadingSpinner, ModalContext } from "@evos/components";
import type { ApolloError } from "@apollo/client";

type EnergyPrioritiesProps = {
  solarChargingStrategy: "solar_charging" | "zero_export" | "no_strategy" | null | undefined;
  optimisationStrategy: "soc" | "static" | "dynamic" | null | undefined;
  prioritiesLoading: boolean;
  onInputPriorityChange: (priority: "solar_charging" | "zero_export" | "no_strategy") => void;
  onOutputPriorityChange: (priority: "soc" | "static" | "dynamic") => void;
  inputPriorityChangeLoading: boolean;
  outputPriorityChangeLoading: boolean;
  inputPriorityChangeError: ApolloError | undefined;
  outputPriorityChangeError: ApolloError | undefined;
};

export default function EnergyPriorities({
  solarChargingStrategy,
  optimisationStrategy,
  prioritiesLoading,
  onInputPriorityChange,
  onOutputPriorityChange,
  inputPriorityChangeLoading,
  outputPriorityChangeLoading,
  inputPriorityChangeError,
  outputPriorityChangeError,
}: EnergyPrioritiesProps) {
  const { handleModal } = useContext(ModalContext);

  const inputPriorityModalOptions = {
    title: "Input priority",
    size: "lg" as const,
    cancelButtonText: "Close",
    content: (
      <div>
        <p><b>Solar Match:</b> Prefer charging when solar generation is available.</p>
        <p><b>Zero Import:</b> Avoid importing from the grid when possible.</p>
        <p className="mb-0"><b>Any:</b> No specific input preference.</p>
      </div>
    ),
  };

  const outputPriorityModalOptions = {
    title: "Output priority",
    size: "lg" as const,
    cancelButtonText: "Close",
    content: (
      <div>
        <p className="mb-0"><b>Vehicle SoC:</b> Allocate based on vehicle state of charge.</p>
        <p><b>Static:</b> Fixed allocation per charger.</p>
        <p><b>Dynamic:</b> Allocation varies with demand.</p>
      </div>
    ),
  };

  return (
    <div id="EnergyPriorities">
      <h3 className="fw-medium">Energy priorities</h3>
      <Row className="my-3 row-gap-3">
        <Col xs={12} md={6}>
          <Card className="p-4">
            <div className="d-flex flex-wrap align-items-center gap-2">
              <h6 className="mb-0 fw-medium">Input priority</h6>
              <Dropdown
                onSelect={(eventKey) => onInputPriorityChange((eventKey ?? "no_strategy") as "solar_charging" | "zero_export" | "no_strategy")}
                style={{ cursor: "pointer" }}
              >
                <Dropdown.Toggle as="h6" className="mb-0 fw-normal" variant="transparent">
                  <span className="pe-1">
                    {!prioritiesLoading && solarChargingStrategy != null
                      ? solarChargingStrategy === "solar_charging"
                        ? <BsSun />
                        : solarChargingStrategy === "zero_export"
                          ? <Bs0Circle />
                          : <BsXCircle />
                      : null}
                  </span>
                  {prioritiesLoading || solarChargingStrategy == null
                    ? <LoadingSpinner />
                    : solarChargingStrategy === "no_strategy"
                      ? "Any"
                      : solarChargingStrategy === "zero_export"
                        ? "Zero Import"
                        : "Solar Match"}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item eventKey="solar_charging"><BsSun className="me-1" /> Solar Match</Dropdown.Item>
                  <Dropdown.Item eventKey="zero_export"><Bs0Circle className="me-1" /> Zero Import</Dropdown.Item>
                  <Dropdown.Item eventKey="no_strategy"><BsXCircle className="me-1" /> Any</Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
              {inputPriorityChangeLoading && <LoadingSpinner />}
              <div role="button" className="ms-auto" onClick={() => handleModal(inputPriorityModalOptions)}>
                <BsInfoCircleFill size={17} color="rgba(0, 4, 26, 0.8)" />
              </div>
            </div>
            {inputPriorityChangeError != null && (
              <Alert variant="danger" className="mt-3 mb-0" message="Failed to update input priority." dismissible showIcon />
            )}
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card className="p-4">
            <div className="d-flex flex-wrap align-items-center gap-2">
              <h6 className="mb-0 fw-medium">Output priority</h6>
              <Dropdown
                onSelect={(eventKey) => onOutputPriorityChange((eventKey ?? "static") as "soc" | "static" | "dynamic")}
                style={{ cursor: "pointer" }}
              >
                <Dropdown.Toggle as="h6" className="mb-0 fw-normal" variant="transparent">
                  <span className="pe-1">
                    {!prioritiesLoading && optimisationStrategy != null
                      ? optimisationStrategy === "soc"
                        ? <BsBatteryHalf />
                        : optimisationStrategy === "static"
                          ? <BsBox />
                          : <BsWater />
                      : null}
                  </span>
                  {prioritiesLoading || optimisationStrategy == null
                    ? <LoadingSpinner />
                    : optimisationStrategy === "soc"
                      ? "Vehicle SoC"
                      : optimisationStrategy === "static"
                        ? "Static"
                        : "Dynamic"}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item eventKey="soc"><BsBatteryHalf className="me-1" /> Vehicle SoC</Dropdown.Item>
                  <Dropdown.Item eventKey="static"><BsBox className="me-1" /> Static</Dropdown.Item>
                  <Dropdown.Item eventKey="dynamic"><BsWater className="me-1" /> Dynamic</Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
              {outputPriorityChangeLoading && <LoadingSpinner />}
              <div role="button" className="ms-auto" onClick={() => handleModal(outputPriorityModalOptions)}>
                <BsInfoCircleFill size={17} color="rgba(0, 4, 26, 0.8)" />
              </div>
            </div>
            {outputPriorityChangeError != null && (
              <Alert variant="danger" className="mt-3 mb-0" message="Failed to update output priority." dismissible showIcon />
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
}
